/**
 * Explore — Generic Booking-Type CTA Strip (2026-08-17)
 *
 * Two persistent, always-shown tiles that link out to affiliate
 * booking pages for car rentals and flights.  Backend source:
 *   GET /api/accommodations/generic-cards
 *
 * The tiles are rendered as plain <a href> so the affiliate URL is
 * preserved byte-for-byte (no `campaign=` mutation, no wrapper).
 *
 * Cardinal Rule: this is an EXPLORE-side component.  It is NOT
 * imported by the Atlas surface.  Filter pills / accommodation-type
 * filters do NOT hide these tiles — they are always visible when
 * their global admin card is active.
 */
import { useEffect, useState } from "react";
import { Car, Plane } from "lucide-react";
import { api } from "@/lib/api";

const ICON_FOR = {
  car_rental: Car,
  flight:     Plane,
};

const LABEL_FOR = {
  car_rental: "Rent a car",
  flight:     "Book a flight",
};

export default function GenericBookingTilesStrip() {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/accommodations/generic-cards");
        if (!cancelled) setCards(data.cards || []);
      } catch (e) {
        // Silent fail — strip disappears if the endpoint 401s
        // (unauthenticated visitors) or the DB is down.
        if (!cancelled) setCards([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading || cards.length === 0) return null;

  return (
    <section
      data-testid="explore-generic-tiles-strip"
      className="rounded-2xl border border-tb-navy/10 bg-white p-4 md:p-5"
    >
      <div className="flex items-baseline justify-between mb-3">
        <p className="font-serif-it italic text-tb-navy text-lg md:text-xl">
          also plan your trip
        </p>
        <span className="font-hand text-[11px] text-tb-navy/50">
          affiliate links
        </span>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {cards.map((c) => {
          const Icon = ICON_FOR[c.booking_type] || Car;
          const cta  = LABEL_FOR[c.booking_type] || c.display_name;
          return (
            <a
              key={c.property_key}
              href={c.custom_booking_url}
              target="_blank"
              rel="noopener nofollow noreferrer"
              data-testid={`explore-generic-tile-${c.booking_type}`}
              className="group flex items-center gap-3 rounded-xl border-2 border-tb-navy/12 bg-tb-cream-3/40 hover:border-tb-gold hover:bg-white hover:shadow-sm transition-all p-4 tb-tap"
            >
              <div className="w-11 h-11 rounded-full bg-tb-navy text-tb-gold-bright flex items-center justify-center shrink-0 group-hover:bg-tb-gold group-hover:text-tb-navy transition-colors">
                <Icon className="w-5 h-5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-hand text-tb-navy font-semibold text-sm truncate">
                  {c.display_name}
                </div>
                <div className="font-hand text-[11px] text-tb-navy/60">
                  {cta} · earns 1× points
                </div>
              </div>
            </a>
          );
        })}
      </div>
    </section>
  );
}
